import React, { useEffect, useLayoutEffect, useRef } from 'react'
import Quill, { EmitterSource } from 'quill'
import MagicUrl from 'quill-magic-url'
import 'quill/dist/quill.snow.css'

interface IQuillEditorProps {
  value: string
  placeholder?: string
  id?: string
  className?: string
  readOnly?: boolean
  magicUrl?: boolean
  onChange?: (content: string, quill: Quill, source: EmitterSource) => void
  onBlur?: (quill: Quill) => void
  toolbarOptions?: (
    | string[]
    | {
        list: string
      }[]
  )[]
}

Quill.register('modules/magicUrl', MagicUrl)

export const QuillEditor = ({
  value,
  placeholder,
  id,
  className,
  readOnly,
  magicUrl = true,
  onChange,
  onBlur,
  toolbarOptions,
}: IQuillEditorProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const quillRef = useRef<Quill | null>(null)
  const onChangeRef = useRef(onChange)
  const onBlurRef = useRef(onBlur)
  const valueRef = useRef(value)
  const placeholderRef = useRef(placeholder)
  const toolbarOptionsRef = useRef(toolbarOptions)
  const magicUrlRef = useRef(magicUrl)
  const isUserInputRef = useRef(false)

  useLayoutEffect(() => {
    onChangeRef.current = onChange
    onBlurRef.current = onBlur
  })

  // Update editor content when value prop changes
  useEffect(() => {
    if (quillRef.current && value !== valueRef.current) {
      valueRef.current = value

      // Only restore cursor position if this is not from user input
      if (!isUserInputRef.current) {
        // Store current cursor position
        const range = quillRef.current.getSelection()

        quillRef.current.setContents(quillRef.current.clipboard.convert({ html: value }))

        // Restore cursor position if it was valid
        if (range && range.index >= 0) {
          // Ensure the index doesn't exceed the new content length
          const newLength = quillRef.current.getLength()
          const adjustedIndex = Math.min(range.index, newLength - 1)
          quillRef.current.setSelection(adjustedIndex, range.length)
        }
      } else {
        // Reset the flag for next external update
        isUserInputRef.current = false
      }
    }
  }, [value])

  useEffect(() => {
    quillRef.current?.enable(!readOnly)
  }, [quillRef, readOnly])

  useEffect(() => {
    const container = containerRef.current
    if (container && !quillRef.current) {
      const editorContainer = container.appendChild(container.ownerDocument.createElement('div'))
      const quill = new Quill(editorContainer, {
        theme: 'snow',
        modules: {
          magicUrl: magicUrlRef.current,
          toolbar: toolbarOptionsRef.current,
        },
        placeholder: placeholderRef.current ?? '',
      })

      quillRef.current = quill

      if (valueRef.current) {
        quillRef.current.setContents(quillRef.current.clipboard.convert({ html: valueRef.current }))
      }

      quillRef.current.on(Quill.events.TEXT_CHANGE, (delta, oldDelta, source) => {
        // Mark that this change is from user input
        if (source === 'user') {
          isUserInputRef.current = true
        }
        const content = quillRef.current?.root.innerHTML || ''
        onChangeRef.current?.(content, quillRef.current as Quill, source)
      })

      quillRef.current.on(Quill.events.SELECTION_CHANGE, (range, oldRange, source) => {
        if (range) {
          if (range.length == 0) {
            // console.log('User cursor is on', range.index)
          } else {
            // const text = quillRef?.current?.getText(range.index, range.length)
            // console.log('User has highlighted', text)
          }
        } else {
          onBlurRef.current?.(quillRef.current as Quill)
        }
      })
    }

    return () => {
      quillRef.current = null
      if (container) {
        container.innerHTML = ''
      }
    }
  }, [])

  return <div ref={containerRef} className={className} id={id}></div>
}
